import { Fragment, FC, memo, ReactNode } from 'react';
import { SxStyleProp } from 'theme-ui';
import { formatTimeNumber } from 'utils/formatTime';

export type TimeScaleProps = {
  variant?: string;
  className?: string;
  min?: number;
  max: number;
  pointsCount?: number;
  formatFn?: (time: number) => string;
};

const styles: SxStyleProp = {
  position: 'relative',
  height: '1.2em',
  fontSize: '12px',
  fontWeight: 'heading',
  color: '#545869',
  userSelect: 'none',
  pointerEvents: 'none',

  '& span, & div': {
    position: 'absolute'
  },

  '& div': {
    top: 'calc(0.6em - 1.5px)',
    height: '3px',
    transform: 'translateX(-50%)',
    borderWidth: '0.5px',
    borderStyle: 'solid',
    borderRadius: '0.5px'
  }
};

const Points: FC<{ pointsCount: number }> = ({ pointsCount }) => {
  const points: ReactNode[] = [];

  for (let i = 1; i < pointsCount - 1; i++) {
    const left = (100 * i) / (pointsCount - 1);
    const mark = <div key={i} style={{ left: `${left}%` }} />;
    points.push(mark);
  }

  return <Fragment>{points}</Fragment>;
};

export const TimeScale: FC<TimeScaleProps> = memo(
  ({ min = 0, max, pointsCount, formatFn = formatTimeNumber, className, variant = 'primary' }) => (
    <div
      className={className}
      sx={{ ...styles, variant: `timeScales.${variant}` }}
      draggable="false"
      data-test="timeScale"
    >
      <span sx={{ left: 0 }} draggable="false">
        {formatFn(min)}
      </span>
      {pointsCount && <Points pointsCount={pointsCount} />}
      <span sx={{ right: 0 }} draggable="false">
        {formatFn(max)}
      </span>
    </div>
  )
);
